import { gql, useQuery } from "@apollo/client"
import useThrottle from "@rooks/use-throttle"
import useTimeoutWhen from "@rooks/use-timeout-when"
import Axios from "axios"
import { Voting_Question } from "client/graphql"
import { groupBy } from "lodash"
import { useEffect, useRef, useState } from "react"

export const MUTATION_VOTE = gql`
  mutation Vote(
    $channelId: String!
    $questionId: String!
    $votes: [Voting_VotesInput!]!
  ) {
    voting_castVote(
      channelId: $channelId
      questionId: $questionId
      votes: $votes
    )
  }
`

export type UseBatchedVoteConfig = {
  batchDelay?: number
  throttleDelay?: number
  maxBatchSize?: number
}

export type Vote = {
  answerId: string
  questionId: string
  count: number
}

/*
 * This hook performs two functions:
 *
 * 1. It batches requests to the server at a rate of `config.batchDelay` so that button
 * mashing doesn't produce excessive load on the server.
 *
 * 2. It throttles the rate at which votes can be cast so that holding down <Enter> on the
 * button or using an auto-clicker doesn't produce excessive vote counts.
 */
export function useBatchedVote(
  channelId: string,
  castVoteEndpoint: string,
  {
    batchDelay = 1000,
    throttleDelay = 100,
    maxBatchSize = 10,
  }: UseBatchedVoteConfig = {},
) {
  // TEMP: Revert to graphql endpoint after MTV show
  const castVote = (data: {
    channelId: string
    questionId: string
    votes: Array<{
      answerId: string
      count: number
    }>
  }) => {
    return Axios.post(castVoteEndpoint, data).catch((_err) => {
      /* ignore errors */
    })
  }
  const voteTotals = useRef<Record<string, Vote>>({})

  // We could do this with a second `useThrottle` but throttled functions
  // fire immediately and then limit the rate of calling to config.batchDelay
  // and we want the initial call to also be delayed by config.batchDelay
  const [startSubmit, setStartSubmit] = useState(false)
  useTimeoutWhen(
    () => {
      // Reorganize into a record of arrays by questionId. It's unlikely we will
      // end up with more than one questionId here but just in case, ensure that
      // answers are being cast against the correct question.
      Object.entries(
        groupBy(voteTotals.current, (vote) => vote.questionId),
      ).forEach(([questionId, votes]) => {
        castVote({
          channelId,
          questionId,
          votes: votes.map((vote) => {
            return {
              answerId: vote.answerId,
              count: Math.min(vote.count, maxBatchSize),
            }
          }),
        })
      })

      voteTotals.current = {}
      setStartSubmit(false)
    },
    batchDelay,
    startSubmit,
  )

  const vote = (questionId: string, answerId: string) => {
    if (!voteTotals.current[answerId]) {
      voteTotals.current[answerId] = {
        answerId,
        questionId,
        count: 0,
      }
    }

    voteTotals.current[answerId].count += 1

    if (!startSubmit) {
      setStartSubmit(true)
    }
  }
  const [throttledVote] = useThrottle(vote, throttleDelay)

  return throttledVote as typeof vote
}

export const QUERY_ACTIVE_QUESTION = gql`
  query GetActiveQuestion($channelId: String!) {
    voting_activeQuestion(channelId: $channelId) {
      id
      text
      maxVotesAllowed
      answers {
        id
        text
      }
    }
  }
`

const SUBSCRIPTION_ACTIVE_QUESTION_UPDATED = gql`
  subscription ActiveQuestionUpdated($channelId: String!) {
    voting_activeQuestionUpdated(channelId: $channelId) {
      id
      text
      maxVotesAllowed
      answers {
        id
        text
      }
    }
  }
`

/**
 * @param {string} channelId
 * @param {boolean} skip whether to skip the query & return null. eg when voting is inactive
 */
export const useActiveQuestion = (
  channelId,
  skip = false,
): Voting_Question | undefined => {
  const query = useQuery(QUERY_ACTIVE_QUESTION, {
    variables: { channelId },
    skip,
  })

  useEffect(() => {
    if (!skip) {
      query.subscribeToMore({
        document: SUBSCRIPTION_ACTIVE_QUESTION_UPDATED,
        variables: { channelId },
        updateQuery: (prev, { subscriptionData }) => {
          return {
            voting_activeQuestion:
              subscriptionData.data.voting_activeQuestionUpdated,
          }
        },
      })
    }
  }, [skip])

  return query?.data?.voting_activeQuestion
}
